import { sectionLabels } from "../section-labels";
import { colorFields, eyebrowField } from "./_shared";
/**
 * FAQ section — accordion of frequently-asked questions. Each Q+A is a
 * `block` so the seller can add as many as they need. Buyer renders the
 * accordion AND emits `FAQPage` JSON-LD so Google can pull rich
 * snippets — high SEO leverage for storefront pages where the seller
 * answers common pre-purchase questions.
 *
 * The schema's `blocks` declaration powers the editor's block-list UI
 * (Phase 2 added a placeholder for "sub-blocks management"; this is the
 * first section that actually exercises it).
 */
export const faqSchema = {
    type: "faq",
    name: sectionLabels["faq"],
    description: {
        ar: "أسئلة وأجوبة بتتفتح وتتقفل. وبتضيف بيانات منظّمة بتظهر في نتائج جوجل.",
        en: "Expandable Q&A. Adds rich-snippet structured data for Google.",
    },
    // Locked + home-only: the FAQ is a permanent, always-present home section
    // (like stories / all-products) that the seller controls — edit the Q&A,
    // change colors/heading, hide it, or reorder it — but can't delete. This is
    // what makes it appear in the editor's section list + inspector for every
    // store. (It used to be an optional add-on, so stores that never added it
    // showed an uncontrollable platform-default FAQ on the storefront.)
    // Unlocked: deletable + re-addable from the editor's "+ Add section" picker.
    availableOnTemplates: ["home"],
    // Renders from its own content — nothing on a custom page is missing.
    availableOnCustomPage: true,
    maxPerTemplate: 1,
    settings: [
        {
            id: "variant",
            type: "select",
            ui: "thumbnail",
            label: { ar: "تصميم الأسئلة", en: "FAQ style" },
            helpText: {
                ar: "شكل عرض الأسئلة.",
                en: "How the questions are laid out.",
            },
            options: [
                { value: "faq-split", label: { ar: "عمودين", en: "Two columns" } },
                { value: "faq-centered", label: { ar: "موسّط", en: "Centered" } },
                { value: "faq-stacked", label: { ar: "قائمة", en: "Full-width list" } },
                { value: "faq-cards", label: { ar: "بطاقات", en: "Cards" } },
                { value: "faq-boxed", label: { ar: "صناديق", en: "Boxed" } },
                // Ported from EasyOrders `faq-split-showcase` (nature family,
                // 2026-07-20). Three columns at ≥992px — an intro column (heading,
                // body, pill CTA), a 4:5 photograph, and the accordion — collapsing
                // to intro / photo / accordion stacked below that.
                {
                    value: "faq-showcase",
                    label: { ar: "عرض بصورة", en: "Showcase" },
                },
            ],
            default: "faq-split",
        },
        // Showcase-only content. Scoped to the variant that renders them so the
        // other five layouts do not sprout three controls they ignore — the same
        // rule `about`'s `ctaLabel`/`ctaHref` follow.
        {
            id: "image",
            type: "image",
            label: { ar: "الصورة", en: "Image" },
            helpText: {
                ar: "صورة طولية جنب الأسئلة.",
                en: "A portrait photo beside the questions.",
            },
            focalPoint: true,
            showIf: { variant: ["faq-showcase"] },
        },
        {
            id: "ctaLabel",
            type: "text",
            i18n: true,
            label: { ar: "نص الزر (اختياري)", en: "Button label (optional)" },
            validate: { max: 40 },
            showIf: { variant: ["faq-showcase"] },
        },
        {
            id: "ctaHref",
            type: "url",
            label: { ar: "رابط الزر", en: "Button destination" },
            helpText: {
                ar: "لينك الصفحة اللي الزر يودّي لها.",
                en: "Where the button goes.",
            },
            showIf: { variant: ["faq-showcase"] },
        },
        ...eyebrowField(),
        {
            id: "title",
            type: "text",
            i18n: true,
            label: { ar: "العنوان", en: "Title" },
            validate: { max: 60 },
            default: { ar: "الأسئلة الشائعة", en: "Frequently asked questions" },
        },
        {
            id: "subtitle",
            type: "text",
            i18n: true,
            label: { ar: "العنوان الفرعي", en: "Subtitle" },
            validate: { max: 120 },
        },
        {
            id: "headingLevel",
            type: "headingLevel",
            label: { ar: "مستوى العنوان", en: "Heading level" },
            default: 2,
        },
        ...colorFields(),
    ],
    blocks: [
        {
            type: "faq-item",
            name: { ar: "سؤال وجواب", en: "Q&A pair" },
            description: {
                ar: "سؤال واحد وجوابه.",
                en: "A single question and its answer.",
            },
            maxPerSection: 12,
            settings: [
                {
                    id: "question",
                    type: "text",
                    i18n: true,
                    label: { ar: "السؤال", en: "Question" },
                    validate: { max: 200 },
                },
                {
                    id: "answer",
                    type: "textarea",
                    i18n: true,
                    label: { ar: "الجواب", en: "Answer" },
                    validate: { max: 1000 },
                },
            ],
        },
    ],
};
//# sourceMappingURL=faq.js.map